import * as z from 'zod';

import { jsonParse } from '@lyearn/core/utils/json';
import routes from '@/pages/corporate/routes';
import { RecommendedActionTypeEnum } from '@/types/schema';

export const modalPayloadSchemaMap = {
  [RecommendedActionTypeEnum.AssignContent]: z
    .looseObject({
      contentIds: z.array(z.string()).optional(),
      assignedToUserIds: z.array(z.string()).optional(),
    })
    .optional(),
  [RecommendedActionTypeEnum.CheckinOkr]: z.looseObject({}).optional(),
  [RecommendedActionTypeEnum.CompleteTask]: null,
  [RecommendedActionTypeEnum.CreateFeedback]: null,
  [RecommendedActionTypeEnum.CreateHabit]: z
    .looseObject({
      name: z.string().optional(),
    })
    .optional(),
  [RecommendedActionTypeEnum.CreateKnowledgeDocument]: z.looseObject({}).optional(),
  [RecommendedActionTypeEnum.CreateMeeting]: z
    .looseObject({
      name: z.string().optional(),
      // participantIds: z.array(z.string()).optional(),
      participants: z.array(z.string()).optional(),
    })
    .optional(),
  [RecommendedActionTypeEnum.CreateOkr]: null,
  [RecommendedActionTypeEnum.CreateSurvey]: null,
  [RecommendedActionTypeEnum.CreateTask]: z
    .looseObject({
      title: z.string().optional(),
      assignedToIds: z.array(z.string()).optional(),
      dueDate: z.iso.date().optional(),
      fileId: z.string().optional(),
    })
    .optional(),
  [RecommendedActionTypeEnum.CompleteContent]: null,
  [RecommendedActionTypeEnum.EnrollInCourse]: null,
  [RecommendedActionTypeEnum.EnrollInArticle]: null,
  [RecommendedActionTypeEnum.GiveFeedback]: z
    .looseObject({
      receiverUserId: z.array(z.string()).optional(),
      feedbackMessage: z.string().optional(),
    })
    .optional(),
  [RecommendedActionTypeEnum.GivePraise]: z
    .looseObject({
      message: z.string().optional(),
      receiverUserIds: z.array(z.string()).optional(),
    })
    .optional(),
  [RecommendedActionTypeEnum.RequestFeedback]: z.looseObject({
    requestMessage: z.string().optional(),
    requestedFromUserIds: z.array(z.looseObject({})).optional(),
  }),
  [RecommendedActionTypeEnum.SetupIntegration]: null,
  [RecommendedActionTypeEnum.SetupOneOnOne]: z
    .looseObject({
      name: z.string().optional(),
      participantIds: z.array(z.string()).optional(),
    })
    .optional(),
  [RecommendedActionTypeEnum.SetupProject]: null,
  // [RecommendedActionTypeEnum.ViewArticle]: null,
  // [RecommendedActionTypeEnum.ViewCourse]: null,
  // [RecommendedActionTypeEnum.ViewTask]: null,
  [RecommendedActionTypeEnum.NoAction]: null,
  [RecommendedActionTypeEnum.GoToHabit]: null,
  [RecommendedActionTypeEnum.ViewHabit]: null,
  [RecommendedActionTypeEnum.UpdateMeeting]: null,
  [RecommendedActionTypeEnum.ViewMeeting]: null,
  [RecommendedActionTypeEnum.ViewPraise]: null,
  [RecommendedActionTypeEnum.GoToProject]: null,
  [RecommendedActionTypeEnum.ViewProject]: null,
  [RecommendedActionTypeEnum.MyTasks]: null,
  [RecommendedActionTypeEnum.ViewTask]: null,
  [RecommendedActionTypeEnum.CelebrateWin]: null,
  [RecommendedActionTypeEnum.ExternalLink]: null,
  [RecommendedActionTypeEnum.ViewDeal]: null,
  [RecommendedActionTypeEnum.ViewContact]: null,
  [RecommendedActionTypeEnum.ViewInbox]: null,
  [RecommendedActionTypeEnum.ViewThread]: null,
  [RecommendedActionTypeEnum.SearchEmail]: null,
} as const satisfies Record<RecommendedActionTypeEnum, z.ZodTypeAny | null>;

export type InsightActionModalState = {
  [K in keyof typeof modalPayloadSchemaMap]: (typeof modalPayloadSchemaMap)[K] extends z.ZodTypeAny
    ? {
        type: K;
        payload: z.infer<(typeof modalPayloadSchemaMap)[K]>;
      }
    : {
        type: K;
        payload: undefined;
      };
}[keyof typeof modalPayloadSchemaMap];

type RouteConfig<T extends z.ZodTypeAny> = {
  schema: T;
  getRoute: (payload: z.infer<T>) => string;
};

const createRouteConfig = <T extends z.ZodTypeAny>(config: RouteConfig<T>): RouteConfig<T> =>
  config;

export const routePayloadSchemaMap = {
  [RecommendedActionTypeEnum.AssignContent]: null,
  [RecommendedActionTypeEnum.CheckinOkr]: null,
  [RecommendedActionTypeEnum.CompleteTask]: createRouteConfig({
    schema: z.looseObject({ actionItemId: z.string() }),
    getRoute: (payload) => {
      return routes.TasksIssue(payload.actionItemId);
    },
  }),
  [RecommendedActionTypeEnum.CreateFeedback]: null,
  [RecommendedActionTypeEnum.CreateHabit]: null,
  [RecommendedActionTypeEnum.CreateKnowledgeDocument]: null,
  [RecommendedActionTypeEnum.CreateMeeting]: null,
  [RecommendedActionTypeEnum.CreateOkr]: null,
  [RecommendedActionTypeEnum.CreateSurvey]: null,
  [RecommendedActionTypeEnum.CreateTask]: null,
  [RecommendedActionTypeEnum.CompleteContent]: createRouteConfig({
    schema: z.looseObject({ courseId: z.string() }),
    getRoute: (payload) => {
      return routes.CourseDetails(payload.courseId);
    },
  }),
  [RecommendedActionTypeEnum.EnrollInCourse]: createRouteConfig({
    schema: z.looseObject({ courseId: z.string() }),
    getRoute: (payload) => {
      return routes.CourseDetails(payload.courseId);
    },
  }),
  [RecommendedActionTypeEnum.EnrollInArticle]: createRouteConfig({
    schema: z.looseObject({ articleId: z.string() }),
    getRoute: (payload) => {
      return routes.ArticleViewer(payload.articleId);
    },
  }),
  [RecommendedActionTypeEnum.GiveFeedback]: null,
  [RecommendedActionTypeEnum.GivePraise]: null,
  [RecommendedActionTypeEnum.RequestFeedback]: null,
  [RecommendedActionTypeEnum.SetupIntegration]: null,
  [RecommendedActionTypeEnum.SetupOneOnOne]: null,
  [RecommendedActionTypeEnum.SetupProject]: null,
  // [RecommendedActionTypeEnum.ViewArticle]: createRouteConfig({
  //   schema: z.looseObject({ documentId: z.string() }),
  //   getRoute: (payload) => {
  //     return routes.ArticleViewer(payload.documentId);
  //   },
  // }),
  // [RecommendedActionTypeEnum.ViewCourse]: createRouteConfig({
  //   schema: z.looseObject({ courseId: z.string() }),
  //   getRoute: (payload) => {
  //     return routes.CourseDetails(payload.courseId);
  //   },
  // }),
  // [RecommendedActionTypeEnum.ViewTask]: createRouteConfig({
  //   schema: z.looseObject({ actionItemId: z.string() }),
  //   getRoute: (payload) => {
  //     return routes.TasksIssue(payload.actionItemId);
  //   },
  // }),
  [RecommendedActionTypeEnum.NoAction]: null,
  [RecommendedActionTypeEnum.GoToHabit]: null,
  [RecommendedActionTypeEnum.ViewHabit]: null,
  [RecommendedActionTypeEnum.UpdateMeeting]: null,
  [RecommendedActionTypeEnum.ViewMeeting]: null,
  [RecommendedActionTypeEnum.ViewPraise]: null,
  [RecommendedActionTypeEnum.GoToProject]: null,
  [RecommendedActionTypeEnum.ViewProject]: null,
  [RecommendedActionTypeEnum.MyTasks]: null,
  [RecommendedActionTypeEnum.ViewTask]: null,
  [RecommendedActionTypeEnum.CelebrateWin]: null,
  [RecommendedActionTypeEnum.ExternalLink]: null,
  [RecommendedActionTypeEnum.ViewDeal]: null,
  [RecommendedActionTypeEnum.ViewContact]: null,
  [RecommendedActionTypeEnum.ViewInbox]: null,
  [RecommendedActionTypeEnum.ViewThread]: null,
  [RecommendedActionTypeEnum.SearchEmail]: null,
} as const satisfies Record<RecommendedActionTypeEnum, RouteConfig<z.ZodTypeAny> | null>;

export function getActionPath(
  type: RecommendedActionTypeEnum,
  /** either the path as string or a json stringified config for deriving the path */
  pathOrPrefill: string,
): string | undefined {
  const isPathAsString = pathOrPrefill.startsWith('/');
  if (isPathAsString) {
    // it is a path string
    return pathOrPrefill;
  }

  const routeConfig = routePayloadSchemaMap[type];
  if (!routeConfig) {
    return undefined;
  }

  // it is a path config
  const { data: parsedPayload, error: jsonParseError } = jsonParse(pathOrPrefill);
  if (jsonParseError) {
    return undefined;
  }

  const { data: payload, success } = routeConfig.schema.safeParse(parsedPayload);
  if (!success) {
    return undefined;
  }

  // @ts-expect-error -- TS is inferring as intersection of types here, instead of union
  return routeConfig.getRoute(payload);
}
